// components/Shop/RootCategoryPage.jsx
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import CategoryCard from "../../components/Shop/CategoryCard";
import api from "../../api"; // مسیر را با پروژه خودت هماهنگ کن

const normalizeArray = (data) => (Array.isArray(data) ? data : []);

export default function RootCategoryListPage() {
  const [rootCategories, setRootCategories] = useState([]);
  const [loadingCategory, setLoadingCategory] = useState(false);
  const [error, setError] = useState("");

  const fetchRootCategory = async () => {
    setLoadingCategory(true);
    setError("");
    try {
      const response = await api.get("/products/root-categories/");
      setRootCategories(normalizeArray(response.data));
    } catch (e) {
      setError("خطا در دریافت اطلاعات دسته‌ها");
      console.error(e);
      setRootCategories([]);
    } finally {
      setLoadingCategory(false);
    }
  };

  useEffect(() => {
    fetchRootCategory();
  }, []);

  const categoriesWithDescription = useMemo(() => {
    return rootCategories.map((item) => ({
      ...item,
      description: item?.description?.trim() || "",
    }));
  }, [rootCategories]);

  return (
    <div className="min-h-screen bg-white font-[Vazirmatn,IRANSans,sans-serif] text-gray-800" dir="rtl">
      <div className="mx-auto max-w-7xl px-4 py-6 sm:px-5 sm:py-8 lg:px-6">
        <div className="mb-6 sm:mb-8">
          <h1 className="text-lg font-bold leading-tight text-[#1E3A5F] sm:text-xl md:text-2xl">
            دسته‌های اصلی
          </h1>
          <p className="mt-2 max-w-3xl text-sm leading-7 text-gray-600 sm:text-base">
            یکی از دسته‌های اصلی را انتخاب کنید و وارد صفحه مربوط به همان دسته شوید.
          </p>
        </div>

        {error ? (
          <div className="mb-6 rounded-2xl border border-red-200 bg-red-50 px-4 py-4 text-sm text-red-600">
            {error}
          </div>
        ) : null}

        <section className="mb-8 sm:mb-10">
          <div className="mb-4 flex items-center justify-between gap-3">
            <h2 className="text-sm font-semibold text-gray-900 sm:text-base">
              دسته‌های ریشه
            </h2>
            <span className="text-xs text-gray-500 sm:text-sm">
              {loadingCategory ? "در حال بارگذاری..." : `${rootCategories.length} مورد`}
            </span>
          </div>

          {loadingCategory ? (
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 sm:gap-4 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6">
              {Array.from({ length: 8 }).map((_, index) => (
                <div
                  key={index}
                  className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm"
                >
                  <div className="aspect-square w-full animate-pulse bg-gray-100" />
                  <div className="space-y-2 p-4">
                    <div className="h-4 w-3/4 animate-pulse rounded bg-gray-100" />
                    <div className="h-3 w-1/2 animate-pulse rounded bg-gray-100" />
                  </div>
                </div>
              ))}
            </div>
          ) : rootCategories.length > 0 ? (
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 sm:gap-4 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6">
              {rootCategories.map((category) => (
                <div key={category.id} className="min-w-0">
                  <Link
                    to={`/categories/${category.slug}`}
                    className="block h-full focus:outline-none focus-visible:ring-2 focus-visible:ring-[#1E3A5F] focus-visible:ring-offset-2"
                  >
                    <CategoryCard category={category} />
                  </Link>
                </div>
              ))}
            </div>
          ) : (
            <div className="rounded-2xl border border-gray-200 bg-[#F9FAFB] px-4 py-10 text-center">
              <p className="text-sm text-gray-500">دسته‌ای برای نمایش وجود ندارد</p>
            </div>
          )}
        </section>

        <section className="rounded-2xl border border-gray-200 bg-white shadow-sm">
          <div className="border-b border-gray-100 px-4 py-4 sm:px-5">
            <h2 className="text-base font-semibold text-gray-900">
              توضیحات دسته‌های ریشه
            </h2>
            <p className="mt-1 text-xs leading-6 text-gray-500 sm:text-sm">
              معرفی کوتاه هر دسته برای انتخاب سریع‌تر
            </p>
          </div>

          <div className="space-y-4 px-4 py-4 sm:space-y-5 sm:px-5 sm:py-5">
            {loadingCategory ? (
              Array.from({ length: 4 }).map((_, index) => (
                <article
                  key={index}
                  className="rounded-xl bg-[#F9FAFB] p-4 ring-1 ring-[#E5E7EB]"
                >
                  <div className="mb-3 h-4 w-1/3 animate-pulse rounded bg-gray-100" />
                  <div className="h-3 w-full animate-pulse rounded bg-gray-100" />
                  <div className="mt-2 h-3 w-5/6 animate-pulse rounded bg-gray-100" />
                </article>
              ))
            ) : categoriesWithDescription.length > 0 ? (
              categoriesWithDescription.map((child, index) => (
                <article
                  key={child.id}
                  className="rounded-xl bg-[#F9FAFB] p-4 ring-1 ring-[#E5E7EB]"
                >
                  <div className="mb-2 flex items-start gap-3">
                    <div className="mt-1 h-2.5 w-2.5 shrink-0 rounded-full bg-[#00AFA9]" />
                    <div className="min-w-0">
                      <h3 className="text-sm font-semibold text-[#1E3A5F] sm:text-base">
                        {index + 1}. {child.name}
                      </h3>
                    </div>
                  </div>

                  {child.description ? (
                    <p className="text-sm leading-7 text-gray-600">
                      {child.description}
                    </p>
                  ) : (
                    <p className="text-sm leading-7 text-gray-400">
                      برای این دسته توضیحی ثبت نشده است.
                    </p>
                  )}
                </article>
              ))
            ) : (
              <div className="rounded-xl bg-[#F9FAFB] p-4 text-sm text-gray-500">
                توضیحی برای نمایش وجود ندارد.
              </div>
            )}
          </div>
        </section>
      </div>
    </div>
  );
}
